<template>
  <div id="app">
    <vs-navbar text-white color="#c40f1e" square fixed>
      <template #left>
        <div class="d-flex align-items-center">
          <img src="@/assets/ls-white.png" class="logo">
          <div class="title">
            <h4>Multiview</h4>
            <div>Long Shot</div>
          </div>
        </div>
      </template>
      <template #right>
        <vs-tooltip bottom v-for="v in channels" :key="v.id">
          <vs-button :animateInactive="true" class="px-0" circle transparent dark :active="activeChannels[String(v.id)]" :flat="activeChannels[String(v.id)]" @click="processButton(v)">
            <img src="@/assets/ls-white.png" class="icon" v-if="v.type === 'twitch'">
            <img src="@/assets/apex.png" class="icon" v-else>
          </vs-button>
          <template #tooltip>{{ v.label }}</template>
        </vs-tooltip>

        <vs-tooltip bottom>
          <vs-button :animateInactive="true" class="px-0" circle transparent dark :active="displayChat" :flat="displayChat" @click="displayChat=!displayChat">
            <img src="@/assets/ls-white.png" class="icon">
          </vs-button>
          <template #tooltip>Chat</template>
        </vs-tooltip>
      </template>
    </vs-navbar>

    <div class="page-container d-flex justify-content-center align-items-center text-center" v-if="firstLoaded && !channels">
      <div>
        <h2 class="mb-3">Long Shot Multiview</h2>
        <h4 class="text-white-50">Multiview will return for next live broadcast</h4>
      </div>
    </div>
    <div class="page-container" v-else-if="firstLoaded && channels">
      <div class="space-left-after-chat" :class="[`player-${selectedChannels.length}`,{nochat: !displayChat}]">
        <!-- :class="{'w-50': selectedChannels.length > 1 && !v.default, 'scale-80': selectedChannels.length > 1 && v.default}" -->
        <Player :data="v" :muted="false" v-for="v in channelPlayer" :key="v.id"/>
      </div>
      <TwitchChat :channel="chatChannel" v-if="chatChannel && displayChat"/>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { EventBus } from './EventBus';
import { api } from './api';

import Player from './components/Player.vue';
import TwitchChat from './components/TwitchChat.vue';

let loading;

export default {
  components: { Player, TwitchChat },
  data: () => ({
    firstLoaded: false,
    displayChat: true,

    channels: null,
    selectedChannels: [],
    interval: null,

    hash: null,
  }),
  computed: {
    ...mapState(['loading']),
    activeChannels(){
      if(!this.firstLoaded || !this.channels) return {};
      return this.selectedChannels.reduce((carry, item) => ({...carry, [item]: true}), {});
    },
    channelPlayer(){
      if(!this.firstLoaded || !this.channels) return [];
      return this.selectedChannels.map(o => {
        return this.channels.find(v => v.id === o)
      });
    },
    chatChannel(){
      if(!this.channels) return '';
      const defaultChannel = this.channels.find(o => o.default) || null;
      if(!defaultChannel) return '';
      return defaultChannel.target; 
    }
  },
  watch: {
    loading(val){
      if(!val && !loading) return;
      if(val && loading) return;

      if(!val && loading){
        loading.close();
        return loading = null;
      }

      loading = this.$vs.loading();
    },
  },
  methods: {
    processButton(v){
      if(this.loading) return;

      const exists = this.selectedChannels.find(o => o === v.id) || null;
      if(exists){
        if(this.selectedChannels.length == 1) return EventBus.$emit('error', 'Hold up..', 'Please remain at least 1 channel selected.');
        //if(v.default) return EventBus.$emit('error', 'Hold up..', 'You cannot unselect main broadcast!');

        return this.selectedChannels = this.selectedChannels.filter(o => o !== v.id);
      }

      if(this.selectedChannels.length >= 9) return EventBus.$emit('error', 'Max channel selected', 'Maximum multiple view is 9. Please unselect old one first.');
      this.selectedChannels.push(v.id);
    },
    async fetchChannels(noloading){
      if(!noloading && this.loading) return;
      if(!noloading) this.$store.commit('setLoading', true);

      try {
        const { data } = await api.get('/channels');

        // It disabled!
        if(!data.result){
          this.hash = null;
          this.selectedChannels = [];
          return this.$set(this, 'channels', null);
        }

        // Hash changed. Reset user selection
        if(data.hash !== this.hash){
          this.selectedChannels = [];
          this.$set(this, 'channels', data.channels);

          this.hash = data.hash;

          const defaultCh = this.channels.find(o => o.default) || null;
          if(defaultCh) this.$set(this, 'selectedChannels', [ defaultCh.id ]);

          if(data.warning){
            this.$vs.notification({
              color: 'warning',
              position: 'bottom-left',
              title: data.warning.title,
              text: data.warning.text,
              duration: 10000,
            });
          }
        }
      } catch (error) {
        EventBus.$emit('error', 'Connection failed', error.toString());
      } finally {
        this.firstLoaded = true;
        if(!noloading) this.$store.commit('setLoading', false);
      }
    },
  },
  async mounted(){
    EventBus.$on('error', (title, text) => this.$vs.notification({
      color: 'danger',
      position: 'bottom-center',
      title,
      text,
      duration: 5000,
    }));
    EventBus.$on('success', (title, text) => this.$vs.notification({
      color: 'success',
      position: 'bottom-center',
      title,
      text,
      duration: 5000,
    }));

    this.fetchChannels();
    this.interval = setInterval(() => this.fetchChannels(true), (60 * 2) * 1000);
  },
  async beforeDestroy(){
    EventBus.$off('error');
    EventBus.$off('success');
    clearInterval(this.interval);
  }
}
</script>

<style scoped>
  img.logo{
    max-width: 100%;
    max-height: 30px;
  }
  div.title{
    margin-bottom: 0;
    margin-left: 1rem;
  }
  div.title > h4{
    line-height: 20px;
    margin-bottom: 0;
  }
  div.title > div{
    line-height: 12px;
    font-size: 80%;
    color: rgb(255 255 255 / 80%);
  }

  div.page-container{
    position: relative;
    top: 44px;
    width: 100%;
    height: calc(100vh - 44px);
  }
  .space-left-after-chat{
    position: absolute;
    width: calc(100% - 350px);
    height: calc(100vh - 44px);
    overflow-x: hidden;
    overflow-y: auto;
    top: 0;
    left: 0;

    display: grid;
    grid-template-columns: repeat( auto-fit, minmax(250px, 1fr) );
    align-items: center;
    justify-content: center;
  }
  .space-left-after-chat.nochat{
    width: 100% !important;
  }
  .space-left-after-chat.nochat.player-1{
    grid-template-columns: 80%;
  }

  .space-left-after-chat.player-2{
    grid-template-columns: 50% 50%;
  }
  .space-left-after-chat.player-2 > div{
    aspect-ratio: 16/9;
    height: 56.25%;
  }

  .space-left-after-chat.player-3{
    grid-template-columns: 70% 30%;
  }
  .space-left-after-chat.player-3 > div:first-child{
    grid-row: span 2;
  }

  .space-left-after-chat.player-4{
    grid-template-columns: 50% 50%;
  }

  .space-left-after-chat.player-5{
    grid-template-columns: repeat(3, calc(100% / 3));
  }
  .space-left-after-chat.player-6{
    grid-template-columns: repeat(3, calc(100% / 3));
  }
  .space-left-after-chat.player-7{
    grid-template-columns: repeat(3, calc(100% / 3));
  }
  .space-left-after-chat.player-8{
    grid-template-columns: repeat(3, calc(100% / 3));
  }
  .space-left-after-chat.player-9{
    grid-template-columns: repeat(3, calc(100% / 3));
  }
  /* .space-left-after-chat.player-5 > div:first-child{
    grid-row: span 3;
  } */

  .icon{
    max-width: 16px;
  }
  .scale-80{
    margin: 0 10%;
  }
  .w-30{
    width: 30%;
  }
</style>